import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { BarChart2, Bell, BookOpen, Cpu, FileText, FlaskConical, HelpCircle, Layers, LayoutDashboard, LineChart, ListOrdered, Loader2, LogOut, Moon, Plug, Radar, ShieldAlert, Sun, Users } from "lucide-react";
import { api } from "@/lib/api";
import { AuthProvider, useAuth } from "@/lib/auth";
import AuthPage from "@/pages/AuthPage";
import { Watchlist } from "@/components/Watchlist";
import { ChartTab } from "@/components/ChartTab";
import StrategyBuilder from "@/components/StrategyBuilder";
import BacktestLab from "@/components/BacktestLab";
import PaperTrading from "@/components/PaperTrading";
import Catalog from "@/components/Catalog";
import Clients from "@/components/Clients";
import ChangePassword from "@/components/ChangePassword";
import GuidedTour from "@/components/GuidedTour";
import AssistantChat from "@/components/AssistantChat";
import Dashboard from "@/nexfin/Dashboard";
import BrokerConnect from "@/nexfin/BrokerConnect";
import Strategies from "@/nexfin/Strategies";
import LivePanel from "@/nexfin/LivePanel";
import PositionsOrders from "@/nexfin/PositionsOrders";
import RiskSettings from "@/nexfin/RiskSettings";
import AlertSettings from "@/nexfin/AlertSettings";
import Backtest from "@/nexfin/Backtest";
import Reports from "@/nexfin/Reports";

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "broker", label: "Broker & Data", icon: Plug },
  { id: "strategies", label: "Strategies", icon: Radar },
  { id: "live", label: "Live Trade Panel", icon: LineChart },
  { id: "orbtest", label: "Backtest", icon: FlaskConical },
  { id: "positions", label: "Positions & Orders", icon: ListOrdered },
  { id: "reports", label: "Reports", icon: FileText },
  { id: "risk", label: "Risk Settings", icon: ShieldAlert },
  { id: "alerts", label: "Alert Settings", icon: Bell },
];
const LAB = [
  { id: "chart", label: "Charts", icon: BarChart2 },
  { id: "builder", label: "Strategy Builder", icon: Layers, admin: true },
  { id: "backtest", label: "Backtest Lab", icon: FlaskConical, admin: true },
  { id: "catalog", label: "Catalog", icon: BookOpen },
  { id: "paper", label: "Catalog Bots", icon: Cpu },
  { id: "clients", label: "Clients", icon: Users, admin: true },
];

const useTheme = () => {
  const [light, setLight] = useState(() => localStorage.getItem("nq_theme") === "light");
  useEffect(() => { document.documentElement.classList.toggle("light", light); localStorage.setItem("nq_theme", light ? "light" : "dark"); }, [light]);
  return [light, setLight];
};

function Terminal() {
  const { user, isAdmin, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => { if (!localStorage.getItem("nq_tour_done")) { const t = setTimeout(() => setTourOpen(true), 900); return () => clearTimeout(t); } }, []);
  const [symbol, setSymbol] = useState("NIFTY50");
  const [quotes, setQuotes] = useState([]);
  const [account, setAccount] = useState(null);
  const [btStrategy, setBtStrategy] = useState(null);
  const [light, setLight] = useTheme();
  const [status, setStatus] = useState(null);
  const loadAccount = useCallback(() => api.get("/paper/account").then((r) => setAccount(r.data)).catch(() => {}), []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const b = p.get("broker");
    if (b) { b === "connected" ? toast.success("Alice Blue logged in for today") : toast.error(`Alice Blue login failed: ${p.get("msg") || "unknown error"}`); setTab("broker"); window.history.replaceState({}, "", "/"); }
  }, []);
  useEffect(() => {
    const q = () => api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    const s = () => api.get("/broker/status").then((r) => setStatus(r.data)).catch(() => {});
    q(); s(); loadAccount();
    const a = setInterval(q, 2000), b = setInterval(loadAccount, 5000), c = setInterval(s, 10000);
    return () => { clearInterval(a); clearInterval(b); clearInterval(c); };
  }, [loadAccount]);

  const runBacktest = (s) => { setBtStrategy({ ...s, _k: Date.now() }); setTab("backtest"); };
  const isLab = LAB.some((l) => l.id === tab);
  const feed = !status ? { text: "…", cls: "bg-slate-500" } : status.mode === "LIVE" && status.connected ? { text: `LIVE${status.market_open ? "" : " · CLOSED"}`, cls: "bg-[#22A34A] pulse-dot" } : status.mode === "LIVE" ? { text: "SESSION EXPIRED", cls: "bg-[#FF3B30]" } : { text: "SIMULATED", cls: "bg-amber-400 pulse-dot" };
  const NavBtn = ({ id, label, icon: Icon }) => (
    <button data-testid={`nav-${id}`} onClick={() => setTab(id)} className={`w-full flex items-center gap-2.5 h-9 px-3 rounded-sm text-xs font-semibold transition-colors ${tab === id ? "bg-[#102A4A] text-[#22A34A] border border-[#163056]" : "text-slate-400 hover:text-white hover:bg-[#102A4A] border border-transparent"}`}>
      <Icon className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{label}</span></button>
  );

  return (
    <div className="App min-h-screen bg-[#071525] flex flex-col">
      <header className="h-14 shrink-0 bg-[#0B1F3A]/90 backdrop-blur-md border-b border-[#163056] px-4 flex items-center gap-4 sticky top-0 z-40">
        <div className="flex items-center gap-2" data-testid="brand">
          <img src="/nexfin.svg" alt="NEXFIN" className="w-8 h-8 rounded-sm" />
          <div className="leading-none"><div className="font-display font-bold text-white tracking-tight">NEXFIN <span className="text-[#22A34A]">TRADE</span></div>
            <div className="flex items-center gap-1 mt-0.5"><span className={`w-1.5 h-1.5 rounded-full ${feed.cls}`} /><span className="text-[9px] font-mono text-slate-500 tracking-widest" data-testid="market-status">NSE · {feed.text}</span></div></div>
        </div>
        {["NIFTY50", "BANKNIFTY"].map((s) => { const q = quotes.find((x) => x.symbol === s); return q ? <div key={s} className="hidden lg:flex flex-col leading-tight" data-testid={`ticker-${s.toLowerCase()}`}><span className="text-[10px] font-mono text-slate-500">{q.name}</span><span className="font-mono text-xs"><span className="text-slate-100">{q.price.toLocaleString("en-IN")}</span> <span className={q.change >= 0 ? "text-bull" : "text-bear"}>{q.change_pct >= 0 ? "+" : ""}{q.change_pct}%</span></span></div> : null; })}
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="theme-toggle" onClick={() => setLight(!light)} className="btn-ghost h-8 w-8 px-0" title="Toggle theme">{light ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}</button>
          <div className="hidden md:flex flex-col leading-tight text-right pl-3 border-l border-[#163056]"><span className="text-xs text-slate-100 max-w-[140px] truncate" data-testid="header-user-name">{user?.name}</span><span className={`text-[9px] font-mono tracking-widest ${isAdmin ? "text-[#22A34A]" : "text-slate-500"}`} data-testid="header-user-role">{user?.role?.toUpperCase()}</span></div>
          <ChangePassword />
          <button data-testid="help-tour-button" onClick={() => setTourOpen(true)} className="btn-ghost h-8 w-8 px-0" title="Guided tour"><HelpCircle className="w-3.5 h-3.5" /></button>
          <button data-testid="logout-button" onClick={logout} className="btn-ghost h-8 w-8 px-0" title="Sign out"><LogOut className="w-3.5 h-3.5" /></button>
        </div>
      </header>
      <div className="flex flex-1 min-h-0">
        <aside className="hidden md:flex w-52 shrink-0 flex-col gap-1 p-2 border-r border-[#163056] bg-[#0B1F3A]/60" data-testid="sidebar">
          {NAV.map((n) => <NavBtn key={n.id} {...n} />)}
          <div className="widget-title px-3 pt-4 pb-1">Lab</div>
          {LAB.filter((l) => !l.admin || isAdmin).map((n) => <NavBtn key={n.id} {...n} />)}
        </aside>
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0B1F3A] border-t border-[#163056] flex overflow-x-auto" data-testid="mobile-nav">
          {NAV.map(({ id, icon: Icon }) => <button key={id} data-testid={`mnav-${id}`} onClick={() => setTab(id)} className={`flex-1 min-w-[56px] h-12 grid place-items-center ${tab === id ? "text-[#22A34A]" : "text-slate-500"}`}><Icon className="w-4 h-4" /></button>)}
        </nav>
        <main className="flex-1 min-w-0 flex overflow-hidden">
          {isLab && <Watchlist quotes={quotes} symbol={symbol} onSelect={(s) => { setSymbol(s); setTab("chart"); }} />}
          <section className="flex-1 min-w-0 h-[calc(100vh-3.5rem)] overflow-y-auto p-3 pb-16 md:pb-3" data-testid="main-stage">
            {tab === "dashboard" && <Dashboard go={setTab} />}
            {tab === "broker" && <BrokerConnect />}
            {tab === "strategies" && <Strategies goLive={() => setTab("live")} />}
            {tab === "live" && <LivePanel />}
            {tab === "orbtest" && <Backtest />}
            {tab === "positions" && <PositionsOrders />}
            {tab === "reports" && <Reports />}
            {tab === "risk" && <RiskSettings />}
            {tab === "alerts" && <AlertSettings />}
            {tab === "chart" && <ChartTab symbol={symbol} quotes={quotes} onTraded={loadAccount} />}
            {tab === "catalog" && <Catalog goPaper={() => setTab("paper")} />}
            {tab === "builder" && isAdmin && <StrategyBuilder onBacktest={runBacktest} goPaper={() => setTab("paper")} />}
            {tab === "backtest" && isAdmin && <BacktestLab initial={btStrategy} />}
            {tab === "paper" && <PaperTrading account={account} reload={loadAccount} />}
            {tab === "clients" && isAdmin && <Clients />}
          </section>
        </main>
      </div>
      {tourOpen && <GuidedTour onNavigate={setTab} onClose={() => setTourOpen(false)} />}
      <AssistantChat onNavigate={setTab} />
    </div>
  );
}

function Gate() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#071525] grid place-items-center text-slate-500" data-testid="auth-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!user) return <AuthPage />;
  return <Terminal key={user.id} />;
}

export default function App() {
  return (<AuthProvider><Toaster theme="dark" position="bottom-right" richColors /><Gate /></AuthProvider>);
}
